import React, { useMemo } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Button } from '@/components/ui/button';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { useRebateForecast } from 'src/api/rebates/useRebateForecast';
import useDivisions from 'src/api/organization/useDivisions';
import useSuppliersByIds from 'src/api/accounts-payable/useSuppliersByIds';
import useCoreEnums from 'src/api/core/core-enums/useCoreEnums';
import { REBATES_SYSTEM_ENUMS } from '@erp/shared-types';
import {
  createSupplierIdToNameMap,
  createDivisionIdToNameMap,
} from 'src/utils/rebateForecastingUtils';
import { useRuleTagFiltering } from 'src/hooks/useRuleTagFiltering';
import { useAgreementTagFiltering } from 'src/hooks/useAgreementTagFiltering';
import { useSupplierTagFiltering } from 'src/hooks/useSupplierTagFiltering';
import RebateCustomBreakdownTableCard from '@/components/rebates/RebateCustomBreakdownTableCard';
import {
  TableConfig,
  MultiYearForecastData,
  DimensionType,
  TimeFilterPreset,
} from '@/types/rebateAnalytics';

/** Table config from location state */
interface TempTableConfig {
  id: string;
  primaryXDimension: string;
  secondaryXDimension?: string;
  primaryYDimension: string;
  secondaryYDimension?: string;
  timeFilterPreset?: string;
  label: string;
}

/** Location state shape */
interface TempReportLocationState {
  title: string;
  tables: TempTableConfig[];
}

function TempReportPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Get report config from location state
  const reportState = location.state as unknown as
    | TempReportLocationState
    | undefined;
  const reportTitle = reportState?.title || 'Custom Report';

  // Convert temp table configs to TableConfig format
  // Use location.state directly in dependency to avoid reference changes
  const tableConfigs: TableConfig[] = useMemo(() => {
    const tables = reportState?.tables || [];
    return tables.map((t) => ({
      id: t.id,
      primaryXDimension:
        (t.primaryXDimension as DimensionType) || DimensionType.TIME,
      secondaryXDimension: t.secondaryXDimension as DimensionType | undefined,
      primaryYDimension: t.primaryYDimension as DimensionType,
      secondaryYDimension: t.secondaryYDimension as DimensionType | undefined,
      timeFilterPreset: t.timeFilterPreset as TimeFilterPreset | undefined,
      label: t.label,
    }));
  }, [reportState?.tables]);

  // Year for forecast - hardcoded to 2025 for demo
  const forecastYear = 2025;
  const forecastStartDate = `${forecastYear}-01-01`;
  const forecastEndDate = `${forecastYear}-12-31`;

  // Fetch forecast data
  const { data: forecastData, isLoading: isLoadingForecast } =
    useRebateForecast({
      startDate: forecastStartDate,
      endDate: forecastEndDate,
    });

  // Create MultiYearForecastData structure
  const multiYearForecastData: MultiYearForecastData = useMemo(
    () => ({
      currentYear: forecastYear,
      currentYearData: forecastData ?? null,
      previousYearData: null,
      isCumulative: true,
    }),
    [forecastData],
  );

  // Fetch divisions data
  const { data: divisionsData } = useDivisions(true);

  // Derive supplier IDs from forecast to batch fetch
  const supplierIdsFromForecast = useMemo(() => {
    if (!forecastData) return [] as string[];
    const fromMap = createSupplierIdToNameMap(forecastData);
    return Array.from(fromMap.keys());
  }, [forecastData]);

  // Batch fetch suppliers by IDs
  const { data: suppliersByIds } = useSuppliersByIds(
    supplierIdsFromForecast,
    !!supplierIdsFromForecast.length,
  );

  // Create maps for supplier and division names
  const { supplierIdToName, divisionIdToName } = useMemo(() => {
    const supplierIdToName = new Map<string, string>();
    const divisionIdToName = new Map<string, string>();

    if (suppliersByIds) {
      suppliersByIds.forEach((s) => supplierIdToName.set(s.id, s.name));
    }

    if (divisionsData?.divisions) {
      divisionsData.divisions.forEach((division) => {
        divisionIdToName.set(division.id, division.name);
      });
    }
    if (forecastData) {
      const forecastDivisionMap = createDivisionIdToNameMap(forecastData);
      forecastDivisionMap.forEach((name, id) => {
        if (!divisionIdToName.has(id)) {
          divisionIdToName.set(id, name);
        }
      });
    }

    return { supplierIdToName, divisionIdToName };
  }, [suppliersByIds, divisionsData, forecastData]);

  // Tag filtering hooks
  const { ruleTagsMap } = useRuleTagFiltering(forecastData?.rules);
  const { agreementTagsMap } = useAgreementTagFiltering(
    forecastData?.agreements,
  );
  const { supplierTagsMap } = useSupplierTagFiltering(forecastData?.suppliers);

  // Get tag enums for name mapping
  const { data: rebateRuleTagsEnum } = useCoreEnums({
    name: REBATES_SYSTEM_ENUMS.RULE_TAGS.name,
    includeOptions: true,
  });
  const { data: rebateAgreementTagsEnum } = useCoreEnums({
    name: REBATES_SYSTEM_ENUMS.AGREEMENT_TAGS.name,
    includeOptions: true,
  });
  const { data: supplierTagsEnum } = useCoreEnums({
    name: REBATES_SYSTEM_ENUMS.SUPPLIER_TAGS.name,
    includeOptions: true,
  });

  // Create tag ID to name maps
  const ruleTagIdToName = useMemo(() => {
    const map = new Map<string, string>();
    if (rebateRuleTagsEnum?.edges[0]?.options) {
      rebateRuleTagsEnum.edges[0].options.forEach((tag) => {
        map.set(tag.id, tag.name || 'Unnamed Tag');
      });
    }
    return map;
  }, [rebateRuleTagsEnum?.edges]);

  const agreementTagIdToName = useMemo(() => {
    const map = new Map<string, string>();
    if (rebateAgreementTagsEnum?.edges[0]?.options) {
      rebateAgreementTagsEnum.edges[0].options.forEach((tag) => {
        map.set(tag.id, tag.name || 'Unnamed Tag');
      });
    }
    return map;
  }, [rebateAgreementTagsEnum?.edges]);

  const supplierTagIdToName = useMemo(() => {
    const map = new Map<string, string>();
    if (supplierTagsEnum?.edges[0]?.options) {
      supplierTagsEnum.edges[0].options.forEach((tag) => {
        map.set(tag.id, tag.name || 'Unnamed Tag');
      });
    }
    return map;
  }, [supplierTagsEnum?.edges]);

  // If no tables in state, show error
  if (!reportState || tableConfigs.length === 0) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate({ to: '/rebates/reports' })}
              className="mb-4"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Reports
            </Button>
            <h1 className="text-2xl font-bold">No Report Data</h1>
            <p className="text-sm text-muted-foreground mt-2">
              No report configuration was provided. Please create a report from
              the Reports page.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoadingForecast) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold">{reportTitle}</h1>
          </div>
          <div className="text-center py-12">
            <h2 className="text-lg text-muted-foreground">
              Loading report data...
            </h2>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 w-full min-w-0 max-w-full overflow-hidden">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">{reportTitle}</h1>
        </div>

        <div className="space-y-4 w-full max-w-full min-w-0">
          {tableConfigs.map((config) => (
            <RebateCustomBreakdownTableCard
              key={config.id}
              config={config}
              onUpdate={() => {}}
              onUpdateLabel={() => {}}
              onDelete={() => {}}
              multiYearForecastData={multiYearForecastData}
              isCumulative={true}
              primaryLine="sarima"
              supplierIdToName={supplierIdToName}
              divisionIdToName={divisionIdToName}
              isCollapsed={false}
              onToggleCollapse={() => {}}
              ruleTagIdToName={ruleTagIdToName}
              agreementTagIdToName={agreementTagIdToName}
              supplierTagIdToName={supplierTagIdToName}
              ruleTagsMap={ruleTagsMap}
              agreementTagsMap={agreementTagsMap}
              supplierTagsMap={supplierTagsMap}
              onMoveUp={() => {}}
              onMoveDown={() => {}}
              isFirst={true}
              isLast={true}
              activeFilterCount={0}
              activeFilterNames={[]}
            />
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default TempReportPage;
